import type { PerioChartResponse } from '@/common-components/composed/PerioChart/types';
import type { ClaimWritebackStatus } from '@/react-query/useWritebackStatus';

import type { ImageAnalyses } from '../../../../@types/latestImagesV2';
import type { TClaimMessage } from '../../../../@types/rcm';

export type { PerioChartResponse };

export const TOOTH_LEVEL_FINDING_TYPES = [
  'caries',
  'restorative_adjacent_lucency',
];

export interface SlotTreatment {
  id: number;
  images: { id: number; score: number; teeth: TToothDisplay[] }[];
}
export interface SlotData {
  slot: number;
  treatments: SlotTreatment[];
}

export enum TreatmentGroupKey {
  SRP = 'SRP',
  CROWN = 'CROWN',
}

export type TTreatmentResponse = {
  txId: number;
  toothId: string | null;
  quadrant: 'LL' | 'LR' | 'UL' | 'UR' | null;
  txCode: string;
  txDescription: string;
  txGroupName: string | null;
  txGroupLongName: Record<string, string>;
  claimGroup: TreatmentGroupKey | null;
};

export type SrpBoneLevel = {
  id: string;
  side: 'mesial' | 'distal';
  value: number;
};

export type TToothDisplay = {
  toothId: string;
  caries: string[] | null;
  boneLevelsV2: SrpBoneLevel[] | null;
  restorativeAdjacentLucency: string[] | null;
  dmf: number | null;
  calculus: string[] | null;
};

export type TTxDisplay = {
  txId: number;
  teeth: TToothDisplay[];
};

export type TImageDisplay = {
  id: number;
  sortKey: number;
  /** Crown intraorals: 0 = best, 1 = second, 2 = third; undefined/null otherwise. */
  intraoralOrder?: number | null;
  txs: TTxDisplay[];
};

/** Response from POST claim-summary/findings v8: top-level messages only; treatments have no note/warning/messages. */
export type GetClaimSummaryResponse = {
  images: ImageAnalyses[];
  treatments: TTreatmentResponse[];
  fmx: SlotData[] | null;
  imagesDisplay: TImageDisplay[] | null;
  perioChart: PerioChartResponse | null;
  messages: TClaimMessage[];
  clinicalNote?: string | null;
  patientName?: string;
  providerName?: string;
  dateOfService?: string;
  patientDateOfBirth?: string | null;
};

export type TToothTableData = TToothDisplay & {
  txCodes: Set<string>;
  toothId: string;
};

export type SrpTooth = {
  potentialCalculus: boolean;
  txCodes: string[];
  mesial: number | null;
  distal: number | null;
  toothId: string;
  measurementType: string | null;
  imageType: string | null;
};

export type SimplifiedTreatment = {
  txId: number;
  tooth: TToothDisplay;
};

export type ToothWithFindings = {
  toothId: string;
  findings: {
    caries: string[] | null;
    boneLevels: SrpBoneLevel[] | null;
    restorativeAdjacentLucency: string[] | null;
    dmf: number | null;
  };
};

// Types for batch-findings endpoint
export type TClaimImageWithAnalysis = ImageAnalyses;

/** Request for batch-findings v7: unifiedAppointmentId and optional estimatedVisitId, imagingPatientVideaId (client may pass when available). */
export type TBatchFindingsRequest = {
  appointments: {
    unifiedAppointmentId: number;
    estimatedVisitId?: number | null;
    imagingPatientVideaId?: string;
  }[];
};

export type TBatchFindingsResponse = {
  images: TClaimImageWithAnalysis[];
  appointmentData: {
    unifiedAppointmentId: number;
    treatments: TTreatmentResponse[];
    messages?: TClaimMessage[];
    fmx: SlotData[] | null;
    imagesDisplay: TImageDisplay[] | null;
    allTreatmentsExcluded?: boolean;
    patientName?: string;
    providerName?: string;
    dateOfService?: string;
    payerName?: string;
    payerId?: number;
    writebackStatus?: ClaimWritebackStatus;
    pushedAt?: string;
  }[];
};
